import type { ForwardedRef } from 'react';
import { forwardRef, useEffect, useState } from 'react';

import { VFlex } from '../VFlex';
import { VToggleButton } from '../VToggleButton';
import { BASE_TOGGLE_BUTTON_SIZE } from '../VToggleButton/constant';
import type { ValueWithLabel } from '../type';
import { joinCn } from '../util';
import type { VInnerToggleButtonGroupProps } from './type';

const VInnerToggleButtonGroup = forwardRef(
  <T extends ValueWithLabel>(
    props: VInnerToggleButtonGroupProps<T>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const {
      itemList,
      onChange,
      selectedItemList,
      disabled = false,
      size = BASE_TOGGLE_BUTTON_SIZE.MD,
    } = props;
    const [innerSelectedItemList, setInnerSelectedItemList] =
      useState<T['value'][]>(selectedItemList);

    const onToggleButtonClick = (value: T['value']) => {
      if (innerSelectedItemList.includes(value)) {
        const newSelectedItemList = innerSelectedItemList.filter(
          v => v !== value,
        );
        setInnerSelectedItemList(newSelectedItemList);
      } else {
        setInnerSelectedItemList([...innerSelectedItemList, value]);
      }
    };

    useEffect(() => {
      if (
        JSON.stringify(selectedItemList) !==
        JSON.stringify(innerSelectedItemList)
      ) {
        onChange(innerSelectedItemList);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [innerSelectedItemList]);

    useEffect(() => {
      if (
        JSON.stringify(selectedItemList) !==
        JSON.stringify(innerSelectedItemList)
      ) {
        setInnerSelectedItemList(selectedItemList);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItemList]);

    return (
      <VFlex
        ref={ref}
        className={joinCn('flex-wrap', {
          'min-h-8.5': size === BASE_TOGGLE_BUTTON_SIZE.MD,
          'min-h-7': size === BASE_TOGGLE_BUTTON_SIZE.SM,
        })}
        items={'center'}
        justify={'start'}
        gap={0.25}
      >
        {itemList.map(({ disabled: itemDisabled, label, value }) => {
          const isSelected = !!selectedItemList.find(
            itemValue => itemValue === value,
          );

          return (
            <VToggleButton
              key={value}
              aria-selected={isSelected}
              aria-label={`toggle-button-${value}`}
              isSelected={isSelected}
              disabled={disabled || itemDisabled}
              size={size}
              onClick={() => onToggleButtonClick(value)}
            >
              {label}
            </VToggleButton>
          );
        })}
      </VFlex>
    );
  },
);

export default VInnerToggleButtonGroup;
